<template>
  <div
    :id="`remark-${remark.id}`"
    v-click-outside="{ handler: handleClickOutside, detectIframe: false }"
    :data-liquid-input-field-path="remark.liquidInputFieldPath"
    :data-text-property-value="remark.textPropertyValue"
    :data-testid="testid"
    :type="remarkType"
    class="
      remark-v2
      tw-flex
      tw-flex-col
      tw-gap-2
      tw-px-6
      tw-py-4
      tw-border
      tw-border-solid
      tw-rounded-lg
      tw-group
    "
    :class="outerContainerClasses"
    @click.stop="handleContainerClick"
  >
    <RemarkBody
      :id="remark.id"
      :show-context="showContext"
      :context-text="contextName"
      :context-link="contextUrl"
      :ledger-period-name="remark.ledgerPeriodName"
      :liquid-input-field-path="remark.liquidInputFieldPath"
      :author-name="authorName"
      :created-at="remark.createdAt"
      :edited-at="remark.editedAt"
      :rendered-text="remark.renderedText"
      :attachments="attachments"
      :allowed-actions="{
        edit: remark.allowedActions.edit,
        openInContext: showContext && showContextBtn,
      }"
      :prevent-bubble-unlink="preventBubbleUnlink"
      :show-period="showPeriod"
      :has-check-diff="remark.hashCheckDiff"
      :has-draft="hasDraft"
      :read="remarkRead"
      :done="remark.done"
      :done-at="remark.doneAt"
      :done-by-name="remark.doneByName"
      :todo="remark.todo"
      :part-of="partOf"
      :pdf-annotation="remark.pdfAnnotation"
      @delete-attachment="deleteAttachment"
      @visit-annotation="gotoPdfAnnotation"
    >
      <template v-if="remark.todo" #top-left>
        <ToDo
          :done="remark.done"
          :can-edit="remark.allowedActions.toggleDone && !disableInteraction"
          @toggle="toggleTodo"
        />
      </template>
      <template #before-message>
        <EditableRemarkTagsListV2
          :editing="editingTags"
          :wrap="wrapTags"
          :tag-ids="remark.tagIds"
          :remark-id="remark.id"
          :class="hideSessionClass"
          @stop-editing="stopEditingTags"
        />
      </template>
      <template #actions>
        <RemarkActions
          :allowed-actions="{
            updateTags: canEditTags && !disableInteraction,
            edit: remark.allowedActions.edit && !disableInteraction,
            openInContext: showContextBtn && contextUrl && !disableInteraction,
            remove: remark.allowedActions.remove && !disableInteraction,
            toggleRead: !disableInteraction,
          }"
          :read="read"
          @click="handleRemarkAction"
        />
      </template>
    </RemarkBody>
    <div class="tw-flex tw-flex-col tw-gap-2">
      <div :class="commentsOpacityClass">
        <SfCommentList
          v-if="!!comments.length"
          :toggleable-count="toggleableComments.length"
          :show-mark-as-read="!read"
          @mark-as-read="toggleRead"
        >
          <template #toggleable>
            <SfComment
              v-for="comment in toggleableComments"
              :id="comment.id"
              :key="comment.id"
              :allow-attachment-deletion="
                comment.allowedActions.edit && !disableInteraction
              "
              :attachment-ids="comment.attachmentIds"
              :attachments="comment.attachments"
              :author-name="comment.authorName"
              :commentable-id="remark.id"
              commentable-type="Remark"
              :created-at="comment.createdAt"
              :editing="comment.editing"
              :edited-at="comment.editedAt"
              :read="comment.read"
              :rendered-text="comment.renderedText"
              :text="comment.text"
              :unread-top-offset="comment.isFirstUnread"
              border
              @delete-attachment="
                (attachmentId) =>
                  deleteCommentAttachment(comment.id, attachmentId)
              "
            >
              <template #actions>
                <RemarkActions
                  :allowed-actions="comment.allowedActions"
                  @click="handleCommentAction($event, comment.id)"
                />
              </template>
            </SfComment>
          </template>
          <template #default="slotProps">
            <SfComment
              v-for="comment in visibleComments"
              :id="comment.id"
              :key="comment.id"
              :allow-attachment-deletion="
                comment.allowedActions.edit && !disableInteraction
              "
              :attachment-ids="comment.attachmentIds"
              :attachments="comment.attachments"
              :author-name="comment.authorName"
              :commentable-id="remark.id"
              commentable-type="Remark"
              :created-at="comment.createdAt"
              :editing="comment.editing"
              :edited-at="comment.editedAt"
              :read="comment.read"
              :unread-top-offset="
                comment.isFirstUnread || (comment.first && !slotProps.expanded)
              "
              :unread-bottom-offset="!comment.last"
              :rendered-text="comment.renderedText"
              :text="comment.text"
              :border="comment.last"
              @delete-attachment="
                (attachmentId) =>
                  deleteCommentAttachment(comment.id, attachmentId)
              "
            >
              <template #actions>
                <RemarkActions
                  :allowed-actions="comment.allowedActions"
                  @click="handleCommentAction($event, comment.id)"
                />
              </template>
            </SfComment>
          </template>
        </SfCommentList>
      </div>
      <SfButtonInverted
        v-if="!showNewCommentForm && !disableInteraction"
        class="tw-ms-auto"
        small
        @click="startReply"
      >
        {{ I18n.t('common.reply') }}
      </SfButtonInverted>
      <CommentForm
        v-if="showNewCommentForm"
        commentable-type="Remark"
        :commentable-id="remark.id"
        :follower-ids="remark.followerIds"
        :btn-text="I18n.t('common.reply')"
        text=""
      />
    </div>
  </div>
</template>

<script setup>
import orderBy from 'lodash/orderBy'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { isCurrentWorkflowLocked } from '@app/adapters/silverfin'
import api from '@app/application/communication/sections/communication/api/remark'
import RemarkBody from '@app/shared/vue/components/molecules/remark-v2/remark-body.vue'
import SfComment from '@app/shared/vue/components/molecules/remark-v2/sf-comment.vue'
import SfCommentList from '@app/shared/vue/components/molecules/remark-v2/sf-comment-list.vue'
import ToDo from '@app/shared/vue/components/molecules/remark-v2/to-do.vue'
import { REMARK_ACTIONS } from '@app/application/communication/constants'
import useI18n from '@app/shared/vue/composables/use-I18n'
import CommentForm from '@app/application/communication/sections/communication/components/comments/comment-form.vue'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import { SfLog } from '@app/adapters/logger'
import { SfButtonInverted } from '@app/shared/vue/components/sf-button'
import RemarkActions from '@app/shared/vue/components/molecules/remark-v2/remark-actions.vue'
import trackEvent from '@app/utils/track_event'
import useRemarkDraft from '@app/shared/vue/composables/use-remark-draft'
import { pickAttachmentsByIds } from '@app/shared/vue/components/molecules/remark-v2/utils'
import EditableRemarkTagsListV2 from '@app/shared/vue/components/molecules/remark-v2/editable-remark-tags-list-v2.vue'

const store = useStore()
const I18n = useI18n()
const hideSessionClass = useRedactSessionRecording()

const props = defineProps({
  preventBubbleUnlink: Boolean,
  remark: { type: Object, required: true },
  showContext: Boolean,
  showContextBtn: Boolean,
  showPeriod: Boolean,
  openReply: Boolean,
  showTags: Boolean,
  partOf: { type: String, required: false, default: null },
})

const { message: draftMessage, load: reloadDraft } = useRemarkDraft({
  id: props.remark.id,
  ledgerId: props.remark.ledgerId,
})

const remarkModalWasOpened = ref(false)

const wrapTags = computed(() => props.partOf !== 'inbox')

const testid = computed(() => {
  if (props.remark.todo) {
    if (props.remark.done) {
      return 'remark-v2-done'
    } else {
      return 'remark-v2-todo'
    }
  } else {
    return 'remark-v2-note'
  }
})

const outerContainerClasses = computed(() => {
  const result = []

  if (isOtherCardHighlighted.value) {
    result.push('tw-opacity-50')
  }

  if (props.remark.todo && props.remark.done) {
    result.push('tw-bg-sf-snow-subtle')
  } else {
    result.push('tw-bg-sf-snow-lighter')
  }

  if (highlighted.value) {
    result.push('remark-v2__highlight-shadow', 'tw-border-sf-primary-dark')
  } else {
    result.push('tw-border-gray-300')
  }

  return result
})

const commentsOpacityClass = computed(() => ({
  'tw-opacity-70': props.remark.todo && props.remark.done,
}))

const showNewCommentForm = computed(
  () =>
    props.openReply ||
    store.state.ui.newCommentRemarkIds.includes(props.remark.id)
)
const canEditTags = computed(() => {
  const anyUnarchivedTags = Object.values(
    store.state.remarkTags?.allRemarkTags || {}
  ).some((t) => !t.archived)
  return (
    props.remark.allowedActions.updateTags &&
    (anyUnarchivedTags || props.remark.tagIds.length > 0)
  )
})

const context = computed(() =>
  store.getters.contextByLedgerTypeAndId(
    store.state.ledgerId || props.remark.ledgerId,
    props.remark.contextType,
    props.remark.contextId
  )
)
const contextName = computed(() => context.value.name)
const contextUrl = computed(() => {
  if (context.value.workflowReviewUrl) {
    return context.value.workflowReviewUrl
  }
  if (!context.value.url) {
    return null
  }
  const url = new URL(context.value.url, window.location.origin)
  if (!props.remark.ledgerArchived) {
    const ledgerId = store.state.ledgerId || props.remark.ledgerId
    url.searchParams.set('current_ledger_id', `${ledgerId}`)
  }
  if (isCompanyContext.value || isDocumentContext.value) {
    url.hash = `remark-${props.remark.id}`
  } else {
    url.searchParams.set('anchor', `remark-${props.remark.id}`)
  }
  return url.toString()
})

const hasDraft = computed(
  () => !!draftMessage.value && draftMessage.value !== props.remark.text
)

const highlighted = computed(() => {
  const { type, id } = store.state.ui.highlightedCard
  return type === 'remark' && id === props.remark.id
})

const isOtherCardHighlighted = computed(() => {
  const { id } = store.state.ui.highlightedCard
  return id && !highlighted.value
})

const notification = computed(
  () => store.getters.notificationsByRemarkId[props.remark.id]
)

const read = computed(() => {
  return notification.value ? notification.value.read : true
})

const remarkRead = computed(
  () =>
    read.value || (notification.value && !!notification.value.linkedCommentId)
)

const disableInteraction = isCurrentWorkflowLocked()

async function toggleRead() {
  if (notification.value) {
    await store.dispatch('toggleReadNotification', notification.value.id)
  } else {
    await store.dispatch('toggleRemarkFollow', props.remark.id)
    await store.dispatch('fetchNotifications', Silverfin.currentCompanyId)
  }
  window.eventHub.$emit('resetNotificationCount')
}

const isCompanyContext = computed(() => props.remark.contextType === 'Company')

const isDocumentContext = computed(
  () => props.remark.contextType === 'Document'
)

const remarkType = computed(() => (props.remark.todo ? 'todo' : 'note'))

const createdByBot = computed(() => !!props.remark.botName)

const authorName = computed(() => {
  if (createdByBot.value) {
    return props.remark.botName
  } else {
    return store.getters.userById(props.remark.authorId).name
  }
})

const attachments = computed(() =>
  pickAttachmentsByIds(
    store.getters.attachmentArray,
    props.remark.attachmentIds
  )
)

function deleteAttachment(attachmentId) {
  window.sfConfirm({
    onConfirm: () =>
      store.dispatch('deleteRemarkAttachment', {
        id: props.remark.id,
        attachmentId,
      }),
  })
}

const comments = computed(() => {
  const storeComments = orderBy(
    store.getters.commentsByIds(props.remark.commentIds),
    ['createdAt'],
    ['asc']
  )
  const result = []
  const hasLinkedComment = notification.value?.linkedCommentId !== null
  let linkedCommentSeen = false
  for (const comment of storeComments) {
    linkedCommentSeen ||= comment.id === notification.value?.linkedCommentId
    const commentRead = hasLinkedComment
      ? read.value || !linkedCommentSeen
      : read.value
    const isFirstUnread =
      !commentRead &&
      (result.length === 0 ||
        comment.id === notification.value?.linkedCommentId)
    result.push({
      ...comment,
      authorName: store.getters.userById(comment.authorId).name,
      editing: store.state.ui.editCommentIds.includes(comment.id),
      attachments: pickAttachmentsByIds(
        store.getters.attachmentArray,
        comment.attachmentIds
      ),
      allowedActions: {
        remove: comment.allowedActions.remove && !disableInteraction,
        edit:
          !comment.editing &&
          comment.allowedActions.edit &&
          !disableInteraction,
        openInContext: false,
        markAsRead: false,
      },
      read: commentRead,
      isFirstUnread,
    })
  }
  return result
})

const visibleComments = computed(() => {
  const result = comments.value.slice(-2)
  return result.map((comment, i) => ({
    ...comment,
    first: i === 0,
    last: i !== result.length - 1,
  }))
})

const toggleableComments = computed(() => {
  return comments.value.slice(0, -2)
})

function deleteCommentAttachment(commentId, attachmentId) {
  const comment = comments.value.find((c) => c.id === commentId)
  window.sfConfirm({
    onConfirm: () => {
      store.dispatch('editComment', [
        props.remark.id,
        'Remark',
        commentId,
        {
          fileAttrs: comment.attachmentIds
            .filter((id) => id !== attachmentId)
            .map((id) => ({ id })),
        },
      ])
    },
  })
}

function focusCommentForm({ commentableId, commentableType }) {
  if (commentableId === props.remark.id && commentableType === 'Remark') {
    store.dispatch('addNewCommentRemarkId', props.remark.id)
  }
}

function gotoPdfAnnotation() {
  if (!props.remark.pdfAnnotation) {
    return
  }

  const { xfdfId } = props.remark.pdfAnnotation

  if (props.remark.requestedForOwner) {
    window.eventHub.$emit('selectPdfAnnotation', xfdfId)
  } else {
    window.open(
      `${
        contextUrl.value
      }&annotationId=${xfdfId}&current_ledger_id=${window.Silverfin.currentLedgerId()}`,
      '_blank'
    )
  }
}

function clearHighlightedCard() {
  store.dispatch('unsetHighlightedCard')
  window.eventHub.$emit('clearAnchor')
}

function handleClickOutside() {
  if (highlighted.value) {
    clearHighlightedCard()
  }
}

function handleContainerClick() {
  if (isOtherCardHighlighted.value) {
    clearHighlightedCard()
  }
}

const editingTags = ref(false)

function handleRemarkAction(action, event) {
  switch (action) {
    case 'updateTags':
      editingTags.value = true
      break
    case 'edit':
      openEditRemarkModal()
      break
    case 'openInContext':
      handleOpenInContext(event)
      break
    case 'toggleRead':
      toggleRead()
      break
    case 'remove':
      handleRemove()
      break
  }
}

function stopEditingTags() {
  editingTags.value = false
}

function handleOpenInContext(event) {
  trackClick('context-button')

  const shouldOpenInNewTab = event.metaKey || event.ctrlKey
  if (shouldOpenInNewTab) {
    window.open(contextUrl.value, '_blank')
  } else {
    window.Turbolinks.visit(contextUrl.value)
  }
}

async function destroyMessage(periodScope = null) {
  try {
    await api.destroy(props.remark.id, props.remark.companyId, periodScope)
    window.eventHub.$emit('refreshCommunicationData', {
      action: REMARK_ACTIONS.DESTROYED,
      remark: { id: props.remark.id },
    })
  } catch (e) {
    SfLog.warn('Error while removing message', { extra: { error: e } })
  }
}

function handleRemove() {
  if (props.remark.sticky) {
    window.modalCitizen.openModal('pinned-message-removal-modal', {
      onConfirm: async (allPeriods) => {
        try {
          if (allPeriods) {
            await destroyMessage()
          } else {
            await api.destroyOnwards(props.remark.id, props.remark.companyId)
            window.eventHub.$emit('refreshCommunicationData', {
              action: REMARK_ACTIONS.DESTROYED,
              remark: { id: props.remark.id },
            })
          }
        } catch (e) {
          SfLog.warn('Error while partially removing message', {
            extra: {
              error: e,
            },
          })
        }
      },
    })
  } else if (props.remark.recurringTodoTemplateId !== null) {
    window.modalCitizen.openModal('recurring-todo-removal-modal', {
      onConfirm: destroyMessage,
    })
  } else {
    window.sfConfirm({ onConfirm: destroyMessage })
  }
}

function handleCommentAction(event, id) {
  if (event === 'edit') {
    store.dispatch('addEditCommentId', id)
  } else if (event === 'remove') {
    window.sfConfirm({
      onConfirm: () =>
        store.dispatch('removeComment', [props.remark.id, 'Remark', id]),
    })
  }
}

function loadDraftFromLocalStorage() {
  if (remarkModalWasOpened.value === true) {
    reloadDraft()
    remarkModalWasOpened.value = false
  }
}

function startReply() {
  store.dispatch('addNewCommentRemarkId', props.remark.id)
}

function openEditRemarkModal() {
  remarkModalWasOpened.value = true
  window.modalCitizen.openModal('edit-remark', {
    originalRemark: {
      ...props.remark,
      showTags: props.showTags,
      attachments,
      allRemarkTags: store.state.remarkTags
        ? store.state.remarkTags.allRemarkTags
        : null,
    },
  })
}

function openEditRemarkModalExternal({ remarkId }) {
  if (props.remark.id === remarkId) {
    openEditRemarkModal()
  }
}

function clearHighlightedCardExternal() {
  if (highlighted.value) {
    clearHighlightedCard()
  }
}

async function toggleTodo() {
  await store.dispatch('toggleTodoDone', props.remark.id)
}

function trackClick(label) {
  if (props.partOf) {
    trackEvent(props.partOf, 'click', label)
  }
}

onMounted(() => {
  window.eventHub.$on('modal-closed', loadDraftFromLocalStorage)
  window.eventHub.$on('openEditRemarkModal', openEditRemarkModalExternal)
  window.eventHub.$on('clearHighlightedCard', clearHighlightedCardExternal)
  // focusCommentForm is received by both:
  // RemarkCard: if comment form is not rendered yet, we need to render it
  // CommentForm: if comment form is rendered, focus the correct comment form based on commentable type/id
  window.eventHub.$on('focusCommentForm', focusCommentForm)

  // if highlighted, and in the context of the pdf viewer, select the corresponding annotation on load
  if (highlighted.value && props.remark.requestedForOwner) {
    gotoPdfAnnotation()
  }
})

onBeforeUnmount(() => {
  window.eventHub.$off('modal-closed', loadDraftFromLocalStorage)
  window.eventHub.$off('openEditRemarkModal', openEditRemarkModalExternal)
  window.eventHub.$off('clearHighlightedCard', clearHighlightedCardExternal)
  window.eventHub.$off('focusCommentForm', focusCommentForm)
})
</script>

<style lang="scss">
.remark-v2 {
  &__highlight-shadow {
    box-shadow: 0 20px 40px 0 #47536117;
  }
}
</style>
